/**
 * useEscapeKey
 *
 * Calls `onEscape` when the Escape key is pressed, while `enabled` is true.
 * For custom overlays that can't be a BaseModal (dropdowns, drawers, the
 * sidebar overlay). BaseModal already handles Escape itself — don't add this
 * to one.
 *
 * The listener is only attached while enabled, so a closed overlay costs
 * nothing and never swallows an Escape meant for something else.
 *
 * @example
 * useEscapeKey(() => setIsOpen(false), isOpen);
 */

import { useEffect, useRef } from 'react';

export function useEscapeKey(onEscape: () => void, enabled: boolean = true): void {
  // Ref so callers can pass an inline arrow without re-binding the listener
  const handlerRef = useRef(onEscape);
  handlerRef.current = onEscape;

  useEffect(() => {
    if (!enabled) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handlerRef.current();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [enabled]);
}
